const FALLBACK_TIERS = [
    { name: "Igniting", min_days: 0, multiplier: 1 },
    { name: "Bronze", min_days: 3, multiplier: 1.05 },
    { name: "Silver", min_days: 7, multiplier: 1.1 },
    { name: "Gold", min_days: 14, multiplier: 1.2 },
    { name: "Platinum", min_days: 30, multiplier: 1.35 },
    { name: "Diamond", min_days: 60, multiplier: 1.5 },
];

export function tierBadge(streakInfo, streakCount = 0) {
    const tier = streakInfo?.tier || tierFor(streakCount);
    const multiplier = Number(streakInfo?.multiplier || tier.multiplier || 1);
    return `${tier.name} - ${multiplier.toFixed(2)}x`;
}

export function nextTierText(streakInfo, streakCount = 0) {
    const next = streakInfo?.next_tier || nextTierFor(streakCount);
    if (!next) return "Max tier reached - keep the streak alive";
    const days = Number(next.days_to_go ?? Math.max(0, next.min_days - streakCount));
    return `${days} ${days === 1 ? "day" : "days"} to ${next.name} - unlocks ${Number(next.multiplier).toFixed(2)}x coins`;
}

export function buildWeekDots(series = [], { createdAt = null, hasHistory = true } = {}) {
    const source = series.slice(-7);
    while (source.length < 7) {
        source.unshift({ reps: 0, coins: 0 });
    }
    // Days before the account existed (or any day for a user with zero workouts)
    // render neutral instead of red — a brand-new user shouldn't see a week of
    // "misses" they never had.
    const created = createdAt ? new Date(createdAt) : null;
    const createdDay = created && !Number.isNaN(created.getTime())
        ? created.toISOString().slice(0, 10)
        : null;
    return source.map((item, index) => {
        const date = item.date ? new Date(`${item.date}T00:00:00`) : null;
        const label = date && !Number.isNaN(date.getTime())
            ? date.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1)
            : ["M", "T", "W", "T", "F", "S", "S"][index];
        const reps = Number(item.reps || 0);
        const coins = Number(item.coins || 0);
        let s;
        if (reps > 0) s = 1;
        else if (coins > 0) s = 2;
        else if (!hasHistory || (createdDay && item.date && item.date < createdDay)) s = 3; // idle/neutral
        else s = 0;
        return {
            d: label,
            s,
            today: index === source.length - 1,
        };
    });
}

// Streak days roll over at UTC midnight (server is the source of truth).
// Prefer the server-provided seconds-left; fall back to computing UTC midnight locally.
export function endOfDayLabel(now = new Date(), serverSecondsLeft = null) {
    let ms;
    if (serverSecondsLeft != null && Number.isFinite(Number(serverSecondsLeft))) {
        ms = Math.max(0, Number(serverSecondsLeft) * 1000);
    } else {
        const end = new Date(now);
        end.setUTCHours(24, 0, 0, 0);
        ms = Math.max(0, end.getTime() - now.getTime());
    }
    const hours = Math.floor(ms / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    if (hours <= 0) return `${Math.max(1, minutes)}m left (UTC)`;
    return `${hours}h left (UTC)`;
}

export function buildLadder(streakInfo, streakCount = 0) {
    const current = streakInfo?.tier?.name || tierFor(streakCount).name;
    return FALLBACK_TIERS.slice(1).map((tier) => ({
        label: tier.name === "Platinum" ? "PLAT" : tier.name.toUpperCase(),
        done: streakCount >= tier.min_days,
        current: tier.name === current,
    }));
}

function tierFor(streakCount) {
    return FALLBACK_TIERS.reduce((best, tier) => (
        streakCount >= tier.min_days ? tier : best
    ), FALLBACK_TIERS[0]);
}

function nextTierFor(streakCount) {
    const next = FALLBACK_TIERS.find((tier) => streakCount < tier.min_days);
    if (!next) return null;
    return { ...next, days_to_go: next.min_days - streakCount };
}
